import React, { useState, useEffect, useRef } from 'react';
import { FaPaste, FaUpload } from 'react-icons/fa';
import { lerDadosClienteDeImagem } from '../others/ocrDocumento';
import { useToast } from './ToastContext';

const TIPOS_ACEITOS = ['image/jpeg', 'image/png'];
const TAMANHO_MAXIMO_LEITURA_MB = 10;
const ROTULOS = {
  nome: 'Nome', cpf: 'CPF', cnpj: 'CNPJ', email: 'E-mail', telefone: 'Telefone', cep: 'CEP', cidade: 'Cidade',
  uf: 'UF', logradouro: 'Logradouro', numero: 'Número', complemento: 'Complemento', bairro: 'Bairro',
};

// Estilo dos campos preenchidos pelo print: amarelo até o formulário ser fechado, para o usuário conferir
export const ESTILO_CAMPO_DO_PRINT = { backgroundColor: '#fff8e1', borderColor: '#f0c36d' };

// O CEP do campo ainda é o que veio do print? Nesse caso, sair do campo CEP não deve buscar o endereço na API do CEP:
// ela sobrescreveria o logradouro/bairro/complemento do print (e CEP geral de cidade, como 96600-000, vem sem rua e
// apagaria o que o print trouxe). Se o usuário mudar o CEP, a busca volta a funcionar normalmente.
export const cepVeioDoPrint = (cepAtual, cepDoPrint) => {
  const doPrint = (cepDoPrint || '').replace(/\D/g, '');
  return doPrint.length === 8 && (cepAtual || '').replace(/\D/g, '') === doPrint;
};

const normalizar = (valor) => (valor || '').toString().replace(/\s+/g, ' ').trim().toLowerCase();

// Formulários abertos, do mais antigo ao mais recente: se um modal abrir por cima de outro, só o de cima recebe o Ctrl+V
const abertos = [];

/**
 * Área "Colar print dos dados do cliente": o usuário tira um print da tela onde estão os dados da pessoa
 * (ex.: Win+Shift+S no card do proprietário no portal do DETRAN) e cola aqui com Ctrl+V, sem precisar salvar arquivo.
 * Também aceita escolher uma imagem JPG/PNG. A leitura (OCR) roda no navegador; a imagem não é enviada nem guardada.
 * Usado em todos os formulários de cadastro/edição de cliente.
 *
 * - valoresAtuais: o objeto do formulário. Só os campos que existem nele são preenchidos, e é com ele que se descobre
 *   o que mudou.
 * - onDadosLidos(dados, alterados, duvidas): a tela sobrescreve os campos com `dados` (já no formato das máscaras) e
 *   destaca os `alterados` com ESTILO_CAMPO_DO_PRINT. O aviso (toast) do que foi lido já é dado aqui.
 *
 * Escuta o Ctrl+V na página inteira enquanto estiver montado (formulário aberto), mas só age quando o que foi colado
 * é uma imagem: colar texto num campo continua funcionando normalmente.
 */
function ImportarPrintCliente({ valoresAtuais, onDadosLidos, disabled = false }) {
  const { addToast } = useToast();
  const [lendo, setLendo] = useState(false);
  const [progresso, setProgresso] = useState(0);
  const [etapa, setEtapa] = useState('');
  const [miniatura, setMiniatura] = useState('');
  const ocultarRef = useRef(null);
  // O listener do Ctrl+V é registrado uma vez só: lê sempre a versão mais recente da função de leitura
  const lerRef = useRef(null);

  useEffect(() => () => clearTimeout(ocultarRef.current), []);
  useEffect(() => () => { if (miniatura) URL.revokeObjectURL(miniatura); }, [miniatura]);

  const ler = async (imagem) => {
    if (lendo || disabled) return;
    if (!TIPOS_ACEITOS.includes(imagem.type)) {
      addToast('error', 'Formato não suportado. Cole um print ou escolha uma imagem JPG/PNG.');
      return;
    }
    if (imagem.size > TAMANHO_MAXIMO_LEITURA_MB * 1024 * 1024) {
      addToast('error', `Imagem muito grande. O limite é ${TAMANHO_MAXIMO_LEITURA_MB} MB.`);
      return;
    }

    clearTimeout(ocultarRef.current);
    setMiniatura(URL.createObjectURL(imagem));
    setLendo(true);
    setProgresso(0);
    setEtapa('Iniciando...');
    let concluiu = false;
    try {
      const lido = await lerDadosClienteDeImagem(imagem, (percentual, mensagem) => {
        setProgresso(percentual);
        setEtapa(mensagem);
      });
      concluiu = true;
      // Só os campos que este formulário tem
      const dados = {};
      Object.keys(lido.dados).filter(campo => campo in valoresAtuais).forEach(campo => { dados[campo] = lido.dados[campo]; });
      const duvidas = lido.duvidas.filter(campo => campo in dados);
      if (Object.keys(dados).length === 0) {
        addToast('warning', 'Não encontrei dados de cliente nesse print. Selecione só a área com os dados, de forma nítida, e tente de novo.');
        return;
      }

      const alterados = Object.keys(dados).filter(campo => normalizar(dados[campo]) !== normalizar(valoresAtuais[campo]));
      onDadosLidos(dados, alterados, duvidas);

      const rotulo = (campo) => ROTULOS[campo] || campo;
      const conferir = duvidas.length ? ` Confira com atenção: ${duvidas.map(rotulo).join(', ')}.` : '';
      if (alterados.length === 0) {
        addToast('success', `Print lido: os dados já estavam iguais aos do formulário.${conferir}`);
      } else {
        addToast(
          duvidas.length ? 'warning' : 'success',
          `Print lido! Preenchido: ${alterados.map(rotulo).join(', ')}. Confira os campos em amarelo antes de salvar.${conferir}`
        );
      }
    } catch (error) {
      console.error('Erro ao ler o print do cliente:', error);
      addToast('error', 'Não foi possível ler a imagem. Preencha os dados manualmente.');
    } finally {
      setLendo(false);
      ocultarRef.current = setTimeout(() => setEtapa(''), concluiu ? 1500 : 0);
    }
  };
  lerRef.current = ler;

  useEffect(() => {
    const instancia = {};
    abertos.push(instancia);
    const aoColar = (e) => {
      if (abertos[abertos.length - 1] !== instancia) return;
      const item = Array.from(e.clipboardData?.items || []).find(i => i.kind === 'file' && i.type.startsWith('image/'));
      if (!item) return;
      e.preventDefault();
      lerRef.current(item.getAsFile());
    };
    document.addEventListener('paste', aoColar);
    return () => {
      document.removeEventListener('paste', aoColar);
      abertos.splice(abertos.indexOf(instancia), 1);
    };
  }, []);

  const escolherArquivo = (e) => {
    const escolhido = e.target.files[0];
    e.target.value = ''; // permite escolher a mesma imagem de novo
    if (escolhido) ler(escolhido);
  };

  const bloqueado = lendo || disabled;

  return (
    <div style={estilos.container}>
      <div style={estilos.area}>
        {miniatura && <img src={miniatura} alt="Print colado" style={estilos.miniatura} />}
        <div style={estilos.textos}>
          <strong style={estilos.titulo}>
            <FaPaste /> {lendo ? `Lendo o print... ${progresso}%` : 'Cole aqui o print com os dados do cliente (Ctrl+V)'}
          </strong>
          <span style={estilos.dica}>
            Tire o print só da área com os dados (Win+Shift+S) e aperte Ctrl+V nesta janela. Os campos lidos são
            substituídos e ficam destacados em amarelo para você conferir. A imagem é lida no seu computador e não é enviada nem guardada.
          </span>
        </div>
        <label style={{ ...estilos.botao, ...(bloqueado ? estilos.botaoDesabilitado : {}) }}>
          <FaUpload /> Escolher imagem
          <input type="file" accept="image/jpeg,image/png" onChange={escolherArquivo} disabled={bloqueado} style={{ display: 'none' }} />
        </label>
      </div>

      {etapa && (
        <div style={estilos.progresso} role="progressbar" aria-valuenow={progresso} aria-valuemin={0} aria-valuemax={100}>
          <div style={estilos.trilho}>
            <div style={{ ...estilos.barra, width: `${progresso}%` }} />
          </div>
          <span style={estilos.dica}>{etapa} {progresso}%</span>
        </div>
      )}
    </div>
  );
}

const estilos = {
  container: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' },
  area: {
    display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px', padding: '12px 14px', borderRadius: '8px',
    border: '2px dashed #b9a4e0', backgroundColor: '#f8f5fd',
  },
  miniatura: { maxHeight: '64px', maxWidth: '120px', borderRadius: '4px', border: '1px solid #ddd', objectFit: 'contain', backgroundColor: '#fff' },
  textos: { flex: '1 1 260px', display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 },
  titulo: { display: 'inline-flex', alignItems: 'center', gap: '8px', color: '#4b2a8a', fontSize: '14px' },
  botao: {
    backgroundColor: '#6f42c1', color: '#fff', padding: '8px 14px', borderRadius: '6px', fontSize: '13px',
    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap',
  },
  botaoDesabilitado: { backgroundColor: '#b8c2cc', cursor: 'not-allowed' },
  dica: { fontSize: '12px', color: '#6c757d' },
  progresso: { display: 'flex', flexDirection: 'column', gap: '4px' },
  trilho: { height: '10px', borderRadius: '5px', backgroundColor: '#e3e6ea', overflow: 'hidden' },
  barra: { height: '100%', borderRadius: '5px', backgroundColor: '#6f42c1', transition: 'width 0.3s ease' },
};

export default ImportarPrintCliente;
